import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Button, IconButton, MenuItem, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import DatePicker2 from "../../components/DatePicker2";
import TerritorySelect from "../../components/AsyncSelects/TerritorySelect";
import SalesmanSelect from "../../components/AsyncSelects/SalesmanSelect";
import { DRILL_DIMENSIONS, hasItemLevelFilter } from "./drillDown";
import { shortRange } from "./format";
import { COMPARE_OPTIONS, RANGE_OPTIONS, selectedDates } from "./urlState";
import "./AnalyticsHeader.css";

dayjs.extend(utc);

export { COMPARE_OPTIONS, RANGE_OPTIONS };

export const PAGE_TABS = [
  { key: "summary", label: "Summary" },
  { key: "customers", label: "Customers" },
  { key: "products", label: "Products" },
  { key: "salesmen", label: "Salesmen" },
  { key: "seasons", label: "Seasons" },
];
const ALL_TERRITORIES = { value: null, label: "All territories" };
const ALL_SALESMEN = { value: null, label: "All salesmen" };
const OFFICE = { value: "unassigned", label: "Office / Unassigned" };
const MENU_PROPS = { MenuProps: { sx: { zIndex: 1500 } } };
const labelOf = (options, value) => options.find((item) => item.value === value)?.label || "";
const toDay = (value) => (value ? dayjs.utc(value) : null);

function CustomDates({ title, initial, onApply, onCancel }) {
  const [start, setStart] = useState(() => toDay(initial.start));
  const [end, setEnd] = useState(() => toDay(initial.end));
  const [error, setError] = useState("");
  function apply(event) {
    event.preventDefault();
    if (!start?.isValid() || !end?.isValid()) { setError("Choose a start and an end date."); return; }
    if (end.isBefore(start, "day")) { setError("End date must be on or after start date."); return; }
    onApply(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
  }
  return <form className="sa-custom-dates" aria-label={title} onSubmit={apply}>
    <strong>{title}</strong>
    <DatePicker2 label="Start date" controlledValue={start} setControlledValue={(value) => { setStart(value); setError(""); }} size="small" />
    <DatePicker2 label="End date" controlledValue={end} setControlledValue={(value) => { setEnd(value); setError(""); }} size="small" />
    <Button type="submit" variant="contained" size="small" className="sa-custom-apply">Apply dates</Button>
    <Button type="button" size="small" onClick={onCancel}>Cancel</Button>
    {error && <span className="sa-custom-error" role="alert">{error}</span>}
  </form>;
}

function PageTabs({ active, onSelect }) {
  const listRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  // On narrow screens the tabs scroll sideways; fade the edge that has more tabs behind it.
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const left = list.scrollLeft > 2;
    const right = list.scrollLeft + list.clientWidth < list.scrollWidth - 2;
    setEdges((current) => (current.left === left && current.right === right ? current : { left, right }));
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);
  useEffect(() => {
    const list = listRef.current;
    const selected = list?.querySelector('[aria-selected="true"]');
    if (!list || !selected || list.scrollWidth <= list.clientWidth || typeof list.scrollTo !== "function") return;
    list.scrollTo({ left: selected.offsetLeft - (list.clientWidth - selected.offsetWidth) / 2 });
    measure();
  }, [active, measure]);
  const move = (event, index) => {
    const next = event.key === "ArrowRight" ? index + 1 : event.key === "ArrowLeft" ? index - 1
      : event.key === "Home" ? 0 : event.key === "End" ? PAGE_TABS.length - 1 : null;
    if (next == null) return;
    event.preventDefault();
    const target = PAGE_TABS[(next + PAGE_TABS.length) % PAGE_TABS.length];
    onSelect(target.key);
    document.getElementById(`sales-analytics-tab-${target.key}`)?.focus();
  };
  return <div className={`sa-tabs-wrap${edges.left ? " has-more-left" : ""}${edges.right ? " has-more-right" : ""}`}>
    <div className="sa-tabs" role="tablist" aria-label="Sales analytics views" ref={listRef} onScroll={measure}>
      {PAGE_TABS.map((item, index) => <button type="button" role="tab" key={item.key} id={`sales-analytics-tab-${item.key}`}
        aria-selected={active === item.key} aria-controls={`sales-analytics-panel-${item.key}`} tabIndex={active === item.key ? 0 : -1}
        className={active === item.key ? "is-selected" : ""} onClick={() => onSelect(item.key)} onKeyDown={(event) => move(event, index)}>
        {item.label}
      </button>)}
    </div>
  </div>;
}

export default function AnalyticsHeader({ state, report, onChange, onInfo }) {
  const [editing, setEditing] = useState(null); // "range" | "compare" | null
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const dates = selectedDates(state);
  const comparisonDates = report?.comparison && state.compare !== "none" ? report.comparison : null;
  const territoryValue = state.territory != null ? { value: state.territory, label: state.territoryName } : ALL_TERRITORIES;
  const salesmanValue = state.salesman != null ? { value: state.salesman, label: state.salesmanName } : ALL_SALESMEN;
  const rangeSummary = shortRange(dates.start_date, dates.end_date);
  const compareSummary = state.compare === "none" ? "no comparison"
    : comparisonDates ? `vs ${shortRange(comparisonDates.start_date, comparisonDates.end_date)}` : "";

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  function copyLink() {
    try {
      const done = navigator.clipboard?.writeText(window.location.href);
      if (done?.then) done.then(() => setCopied(true), () => {});
      else setCopied(true);
    } catch (error) {
      // Copying is a convenience; the address bar already holds the link.
    }
  }

  function pickRange(value) {
    if (value === "custom") { setEditing("range"); return; }
    setEditing(null);
    onChange({ range: value, from: null, to: null });
  }
  function pickCompare(value) {
    if (value === "custom") { setEditing("compare"); return; }
    setEditing(null);
    onChange({ compare: value, cfrom: null, cto: null });
  }

  const focus = state.focus;
  return <div className="sa-header">
    <div className="sa-header-row">
      <h1>Sales analytics</h1>
      <Button className="sa-filters-toggle" startIcon={<FilterListIcon />} aria-expanded={filtersOpen} aria-controls="sa-filters"
        onClick={() => setFiltersOpen(!filtersOpen)}>
        <span>{rangeSummary}{compareSummary ? ` ${compareSummary}` : ""}{state.salesman != null ? ` · ${state.salesmanName}` : ""}</span>
      </Button>
      <div id="sa-filters" className={`sa-filters${filtersOpen ? " is-open" : ""}`}>
        <TextField select size="small" label="Dates" className="sa-filter sa-filter--dates" value={editing === "range" ? "custom" : state.range}
          onChange={(event) => pickRange(event.target.value)}
          SelectProps={{ ...MENU_PROPS, renderValue: (value) => <span className="sa-select-value"><b>{labelOf(RANGE_OPTIONS, value)}</b>{editing !== "range" && rangeSummary && <span> · {rangeSummary}</span>}</span> }}>
          {RANGE_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Compare" className="sa-filter sa-filter--compare" value={editing === "compare" ? "custom" : state.compare}
          onChange={(event) => pickCompare(event.target.value)}
          SelectProps={{ ...MENU_PROPS, renderValue: (value) => <span className="sa-select-value"><b>{labelOf(COMPARE_OPTIONS, value)}</b>{editing !== "compare" && comparisonDates && <span> · {shortRange(comparisonDates.start_date, comparisonDates.end_date)}</span>}</span> }}>
          {COMPARE_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}{item.hint && <span className="sa-option-hint">&nbsp;· {item.hint}</span>}</MenuItem>)}
        </TextField>
        <div className="sa-filter sa-filter--territory">
          <TerritorySelect label="Territory" value={territoryValue} extraOptions={[ALL_TERRITORIES]}
            isOptionEqualToValue={(option, value) => option.value === value.value} disableClearable
            onChange={(_event, value) => onChange({ territory: value?.value ?? null, territoryName: value?.value != null ? value.label : null })} />
        </div>
        <div className="sa-filter sa-filter--salesman">
          <SalesmanSelect label="Salesman" value={salesmanValue} extraOptions={[ALL_SALESMEN, OFFICE]}
            isOptionEqualToValue={(option, value) => option.value === value.value} disableClearable
            onChange={(_event, value) => onChange({ salesman: value?.value ?? null, salesmanName: value?.value != null ? value.label : null })} />
        </div>
      </div>
    </div>

    {editing === "range" && <CustomDates title="Custom dates" initial={{ start: dates.start_date, end: dates.end_date }}
      onCancel={() => setEditing(null)}
      onApply={(from, to) => { setEditing(null); onChange({ range: "custom", from, to }); }} />}
    {editing === "compare" && <CustomDates title="Compare with" initial={{ start: state.cfrom || comparisonDates?.start_date, end: state.cto || comparisonDates?.end_date }}
      onCancel={() => setEditing(null)}
      onApply={(cfrom, cto) => { setEditing(null); onChange({ compare: "custom", cfrom, cto }); }} />}

    {focus.length > 0 && <section className="sa-focus" aria-label="Focused on">
      <span className="sa-focus-label">Focused on</span>
      {focus.map((item) => <span className="sa-focus-chip" key={item.key}>
        <span>{DRILL_DIMENSIONS[item.dimension]?.label}</span>
        <strong>{item.name}</strong>
        <IconButton size="small" aria-label={`Remove ${item.name} filter`} onClick={() => onChange({ focus: focus.filter((other) => other.key !== item.key) })}><CloseIcon fontSize="inherit" /></IconButton>
      </span>)}
      <Button size="small" onClick={() => onChange({ focus: [] })}>Clear all</Button>
      {hasItemLevelFilter(focus) && <span className="sa-focus-note">Sales count only the matching items. Delivery fees and order-level adjustments are left out.</span>}
    </section>}

    <div className="sa-tabs-row">
      <PageTabs active={state.tab} onSelect={(tab) => onChange({ tab })} />
      <div className="sa-actions">
        <Button size="small" onClick={copyLink}>{copied ? "Link copied" : "Copy link"}</Button>
        <IconButton size="small" aria-label="How these numbers are calculated" onClick={onInfo} className="sa-info-button">?</IconButton>
      </div>
    </div>
  </div>;
}
